/**
 * Shared Tailwind bundles for Home dashboard — typography, surfaces, motion.
 */

/** Logged-out / simple marketing shell on Home route. */
export const homePageRoot = 'min-h-screen bg-background';

/**
 * Logged-in home root: bleed to shell edges without a background gradient (follows theme surface).
 *
 * Negative side margins cancel App shell px-6/8/10 so the column can match full shell width.
 * Negative top (-mt-4/-mt-5) tucks hero under header spacing similarly to full-bleed hero layouts.
 */
export const homeDashboardPageRoot =
  'min-h-screen -mx-6 sm:-mx-8 lg:-mx-10 -mt-4 sm:-mt-5 bg-background';

/**
 * Dashboard content stack on Home — re-applies the same horizontal padding as the App shell so all
 * content stays pixel-aligned with the header and every other page.
 */
export const homeDashboardColumn =
  'relative z-10 w-full px-6 sm:px-8 lg:px-10';

/**
 * Hero block (welcome + quick actions): no separate full-bleed bar — sits inside {@link homeDashboardColumn}.
 */
export const homeHeroHeader =
  'relative overflow-visible pt-6 sm:pt-8 pb-10 sm:pb-12 mb-2 sm:mb-4';

export const homeHeroTitle =
  'text-[1.85rem] sm:text-4xl lg:text-[2.65rem] font-bold tracking-tight text-text-primary';

export const homeHeroSubtitle =
  'mt-3 max-w-2xl text-[0.9375rem] sm:text-base leading-relaxed font-normal text-text-secondary';

/** Vertical rhythm between stacked sections inside the dashboard column (no max-width / horizontal padding — use parent {@link homeDashboardColumn}). */
export const homeMainStack = 'space-y-10 sm:space-y-12';

export const homeSectionHeading =
  'text-lg sm:text-xl font-bold tracking-tight text-text-primary';

export const quickActionSubtitle =
  'block text-[0.6875rem] sm:text-xs font-medium text-text-secondary leading-snug line-clamp-2';

/** Outer shell for Happening soon / Find next homepage session strips. */
export const homeUpcomingSessionCardShell =
  'rounded-2xl border border-border-light bg-white shadow-brand-md ring-1 ring-black/[0.04] backdrop-blur-sm dark:border-border-medium dark:bg-surface dark:ring-white/[0.04]';

/** Elevated inset surface for rows below hero (optional wrap). */
export const homeElevatedInset =
  'rounded-2xl border border-white/70 bg-surface/95 shadow-brand-md ring-1 ring-black/[0.03] backdrop-blur-sm dark:border-border-medium dark:bg-surface dark:ring-white/[0.04]';

/** Guest merge prompt on Home — solid surface, matches session cards. */
export const homeGuestMergeShell =
  'rounded-2xl border border-border-light bg-surface shadow-brand-md ring-1 ring-black/[0.04] dark:border-border-medium dark:ring-white/[0.04]';

/**
 * Dark navy hero shell — replaces the light welcome header on the authenticated dashboard.
 * Uses a fixed dark palette so it looks the same in both light and dark theme.
 */
export const homeDarkHeroShell =
  'rounded-2xl bg-[#0e1624] text-white shadow-brand-lg ring-1 ring-white/[0.06] overflow-hidden';

/** Stat tile on the dark hero — subtle white-tinted surface with a border. */
export const homeDarkStatTile =
  'rounded-xl bg-white/[0.07] border border-white/[0.1] p-3.5 flex flex-col gap-1.5';

/** Divider rule inside the dark hero (right-column separator on lg). */
export const homeDarkDivider =
  'border-white/[0.12]';
